import { useState, useEffect, CSSProperties } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./Header.css";
import { useCustom } from "../hooks/useCustomization";
import { useAuth } from "../hooks/useAuth";
import { useModules } from "../context/ModulesContext";
import { useDefaultLandingRoute } from "../hooks/useDefaultLandingRoute";
import { isMobileApp } from "../utils/isMobileApp";
import { getAppContext } from "../utils/getAppContext";
import { getBrandConfig } from "../utils/brandConfig";
import { useNotifications } from "../hooks/useNotifications";
import NotificationPanel from "./NotificationPanel";

// Mobile detection — mirrors App.js. Controls App Store compliance gates:
// gear/admin menu hidden, and ?platform=mobile sent to /auth so the Auth0
// hosted page drops the REGISTER button.
const mobileMode =
  isMobileApp() ||
  window.navigator.userAgent.includes("iPhone") ||
  window.navigator.userAgent.includes("iPad");

export default function Header() {
  const { customization, isReady: isCustomizationReady } = useCustom();
  const {
    logout,
    entitlements,
    roles,
    isAuthenticated,
    canSwitchOrgs,
    isPlatformAdminMode,
    org,
  } = useAuth();

  // Sub-admin orgs (any admin org that isn't AskAdmin, e.g. CGAdmin) get their
  // own configurable header banner — name + colour set on AskAdmin when the org
  // is created/edited, delivered via /session. Falls back to the org's own
  // label (never another org's name) on a blue banner if branding wasn't
  // configured. AskAdmin keeps the default green "AskSmart" banner.
  const isSubAdminOrg =
    isPlatformAdminMode &&
    !!org?.orgID &&
    org.orgID !== process.env.REACT_APP_ASK_ADMIN_ORG_ID;
  const adminBannerName =
    org?.bannerName?.trim() || org?.adminLabel?.trim() || "Admin";
  const adminBannerColor = org?.bannerColor?.trim() || "#2563eb";
  const { modules } = useModules();
  const scaffoldedActive = modules.filter(m => m.scaffolded && m.status === 'active');

  console.log("[Header] Auth state:", {
    isAuthenticated,
    entitlements,
  });

  const navigate = useNavigate();
  const location = useLocation();
  const isDev = process.env.NODE_ENV !== "production";

  const [open, setOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);

  // Bell visibility is gated by the SMRT Tasks entitlement on the org —
  // notifications today flow primarily from the task module, so orgs that
  // haven't enabled it shouldn't see the bell at all. (Invites + Docs
  // events still write UserNotification docs server-side, but a user in a
  // non-Tasks org has no surface to read them from until the bell exists,
  // which is fine — those events also send push.)
  const smrtTasksActive =
    entitlements?.features?.["smrtTasks"]?.enabled === true;

  // Bell-icon data. Hook handles polling + auth gating internally, so the
  // header doesn't need to think about when to fetch — it just renders the
  // current state. Unread count drives the badge; clicking the bell toggles
  // the panel which renders the full list.
  const { notifications, unreadCount, markRead, dismiss, clearAll } =
    useNotifications();

  // ── Role helpers ──────────────────────────────────────────────────────────

  const { systemRoles, permissions } = roles;


  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isSuperAdmin = systemRoles.includes("Super-Admin");

  // ── Two-gate access check ─────────────────────────────────────────────────
  //
  // Gate 1 — entitled: org must have the tool enabled. Applies to everyone, no bypass.
  // Gate 2 — can: user holds the required permission keys. No admin bypass.

  const can = (
    entitlementKey: string | null,
    requiredPermissions: string[],
  ): boolean => {
    // Gate 1 — entitlement check (null = no entitlement required, always pass)
    if (entitlementKey !== null) {
      if (entitlements?.features?.[entitlementKey]?.enabled !== true) return false;
    }

    // Gate 2 — permission check
    return requiredPermissions.every((p) => permissions.includes(p));
  };

  // ── Navigation ────────────────────────────────────────────────────────────

  const protectedPaths = [
    "/chat",
    "/chatbot",
    "/manage-chat",
    "/admin",
    "/superadmin",
    "/switch-org",
    "/unauthorized",
    "/orgchart",
    "/waitlist",
    "/profiles",
    // SMRT modules
    "/smrt-docs",
    "/smrt-access",
    "/smrt-cop",
    "/smrt-tasks",
    "/smrt-crmtix",
    // SMRT admin
    "/manage-profiles",
    "/manage-smrt-docs",
    "/manage-smrt-cop",
    "/manage-smrt-tasks",
    "/smrt-timetrack",
    "/login-as",
  ];

  const scaffoldedPaths = scaffoldedActive.flatMap((m) =>
    [m.route, m.adminRoute].filter(Boolean) as string[],
  );
  const allProtectedPaths = [...protectedPaths, ...scaffoldedPaths];
  const isOnProtectedRoute = allProtectedPaths.some(
    (path) =>
      location.pathname === path || location.pathname.startsWith(`${path}/`),
  );

  // Title click previously hardcoded "/chat", which 401'd through the
  // EntitlementGuard whenever the org didn't have chatbot enabled.
  // Now we read the same destination AuthRedirect uses — role default
  // → org default → "chatbot" fallback → first entitled module —
  // and fall back to "/" while the answer isn't ready yet (auth /
  // entitlements / modules still loading). The hook is pure read; no
  // navigation happens from here.
  const { route: defaultLandingRoute, ready: defaultLandingReady } =
    useDefaultLandingRoute();

  const titleHref = isPlatformAdminMode
    ? "#"
    : isOnProtectedRoute && isAuthenticated
      ? defaultLandingReady && defaultLandingRoute
        ? defaultLandingRoute
        : "/"
      : "/";

  const title = customization?.branding?.title || "AskSmart";
  const closeMenus = () => {
    setOpen(false);
    setAdminOpen(false);
  };

  const handleLogout = () => {
    closeMenus();
    logout();
  };
  useEffect(() => {
    console.log("NAV TRACE →", window.location.pathname);
  }, [location.pathname]);
  useEffect(() => {
    console.log("ENTITLEMENTS UPDATED →", entitlements);
  }, [entitlements]);
  const authQuery = mobileMode ? "?platform=mobile" : "";
  // Re-enter via the current brand's door — the Action stamps brand from the
  // initiating client, so the wrong door shows a wrongly-filtered org picker.
  const handleSwitchOrg = () => {
    const authPath = getBrandConfig(getAppContext()).authEndpoint;
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}${authPath}${authQuery}`;
  };
  const handleLogin = () => {
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/auth${authQuery}`;
  };
  const handleSignup = () => navigate("/registration/user");

  // ── Render ────────────────────────────────────────────────────────────────

  // Hide the header while a real customization is being resolved so we don't
  // flash the default green "AskSmart" banner before the org branding lands.
  // Scoped to protected routes: public pages (registration, join-org, etc.)
  // never call fetchSession, so isAuthenticated stays null there forever —
  // without this scope the gate would hide the header on every public page.
  //
  // Platform admin mode is exempt: the admin org has no branding, so
  // customization never resolves there and the gate would hide the header
  // permanently — taking the title bar and nav (incl. Switch Org) with it.
  const isAwaitingCustomization =
    isOnProtectedRoute &&
    isAuthenticated !== false &&
    !isCustomizationReady &&
    !isPlatformAdminMode;

  // A sub-admin org recolours the banner via its configured colour by
  // overriding the header CSS variables inline.
  const headerStyle: CSSProperties = {
    ...(isAwaitingCustomization ? { visibility: 'hidden' } : {}),
    ...(isSubAdminOrg
      ? ({
          '--header-bg-color': adminBannerColor,
          '--header-hover-color': adminBannerColor,
        } as CSSProperties)
      : {}),
  };

  return (
    <header
      className="header"
      style={Object.keys(headerStyle).length ? headerStyle : undefined}
      aria-hidden={isAwaitingCustomization || undefined}
    >
      <div className="header-inner">
        <div className="header-title-wrapper">
          <Link to={titleHref} className="header-title">
            {isSubAdminOrg
              ? adminBannerName
              : isOnProtectedRoute
                ? title
                : "AskSmart"}
            {isDev ? " (Dev)" : ""}
          </Link>
        </div>

        {isAuthenticated && (
          <div className="navbar">
            {/* ── Bell — in-app notification center. Rendered only when
                 the org has the SMRT Tasks module active (task events
                 are the primary driver of bell traffic). Badge shows
                 the unread count. ── */}
            {smrtTasksActive && (
              <>
                <button
                  className="bell"
                  aria-label="Notifications"
                  onClick={() => {
                    setOpen(false);
                    setAdminOpen(false);
                    setBellOpen((prev) => !prev);
                  }}
                >
                  <span aria-hidden>🔔</span>
                  {unreadCount > 0 && (
                    <span className="bell-badge" aria-hidden>
                      {unreadCount >= 100 ? "99+" : unreadCount}
                    </span>
                  )}
                </button>
                {bellOpen && (
                  <NotificationPanel
                    notifications={notifications}
                    onClose={() => setBellOpen(false)}
                    onMarkRead={markRead}
                    onDismiss={dismiss}
                    onClearAll={clearAll}
                  />
                )}
              </>
            )}

            {/* ── Gear — admin/manage tools. Hidden on mobile for App Store
                 compliance (no admin surface inside the iOS app). ── */}
            {!mobileMode && permissions.some(p => p.includes('.admin') || p.includes('.manage')) && (
            <button
              className="gear"
              onClick={() => {
                setOpen(false);
                setAdminOpen((prev) => !prev);
                setBellOpen(false);
              }}
            >
              ⚙️
            </button>
            )}

            {/* ── Hamburger — operational tools ── */}
            <button
              className="hamburger"
              onClick={() => {
                setAdminOpen(false);
                setBellOpen(false);
                setOpen((prev) => !prev);
              }}
            >
              <span />
              <span />
              <span />
            </button>

            {/* ── Operational menu ── */}
            {open && (
              <div className="menu" onMouseLeave={() => setOpen(false)}>
                {isPlatformAdminMode ? (
                  <>
                    {canSwitchOrgs && (
                      <button onClick={handleSwitchOrg}>Switch Org</button>
                    )}
                    <button onClick={handleLogout}>Logout</button>
                  </>
                ) : (
                  <>
                    {can("chatbot", ["chatbot"]) && (
                      <Link to="/chat" onClick={() => setOpen(false)}>
                        {title}
                      </Link>
                    )}

                    {can("smrtDocs", ["smrtdocs.view"]) && (
                      <Link to="/smrt-docs" onClick={() => setOpen(false)}>
                        SMRT Docs
                      </Link>
                    )}

                    {can("smrtCop", ["cop.view"]) && (
                      <Link to="/smrt-cop" onClick={() => setOpen(false)}>
                        SMRT COP
                      </Link>
                    )}

                    {can("smrtTasks", ["smrttasks.view"]) && (
                      <Link to="/smrt-tasks" onClick={() => setOpen(false)}>
                        SMRT Tasks
                      </Link>
                    )}

                    {can("smrtHost", ["smrtHost"]) && (
                      <Link to="/waitlist" onClick={() => setOpen(false)}>
                        SMRT Host
                      </Link>
                    )}

                    {can("profiles", ["profiles.view"]) && (
                      <Link to="/profiles" onClick={() => setOpen(false)}>
                        Profiles
                      </Link>
                    )}

                    {scaffoldedActive
                      .filter(m => m.route && m.viewPermission)
                      .map(m => can(m.entitlementKey, [m.viewPermission!]) && (
                        <Link key={m.id} to={m.route!} onClick={() => setOpen(false)}>
                          {m.name}
                        </Link>
                      ))}

                    {canSwitchOrgs && (
                      <button onClick={handleSwitchOrg}>Switch Org</button>
                    )}
                    <button onClick={handleLogout}>Logout</button>
                  </>
                )}
              </div>
            )}

            {/* ── Admin / Gear menu ── */}
            {adminOpen && (
              <div
                className="admin-menu"
                onMouseLeave={() => setAdminOpen(false)}
              >
            
                {can(null, ["users.manage"]) && (
                  <Link to="/admin/users" onClick={() => setAdminOpen(false)}>
                    Users
                  </Link>
                )}

                {can("chatbot", ["chatbot.admin"]) && (
                  <Link to="/manage-chat" onClick={() => setAdminOpen(false)}>
                    Manage Chat
                  </Link>
                )}


                {can(null, ["subscriptions.manage"]) && (
                  <Link
                    to="/admin/subscriptions"
                    onClick={() => setAdminOpen(false)}
                  >
                    Subscriptions
                  </Link>
                )}

                {can("orgChart", ["orgChart"]) && (
                  <Link to="/orgchart" onClick={() => setAdminOpen(false)}>
                    Org Chart
                  </Link>
                )}

                {can("profiles", ["profiles.admin"]) && (
                  <Link
                    to="/manage-profiles"
                    onClick={() => setAdminOpen(false)}
                  >
                    Manage Profiles
                  </Link>
                )}

                {can("smrtDocs", ["smrtdocs.admin"]) && (
                  <Link
                    to="/manage-smrt-docs"
                    onClick={() => setAdminOpen(false)}
                  >
                    Manage SMRT Docs
                  </Link>
                )}

                {can("smrtCop", ["cop.admin"]) && (
                  <Link
                    to="/manage-smrt-cop"
                    onClick={() => setAdminOpen(false)}
                  >
                    Manage SMRT COP
                  </Link>
                )}

                {can("smrtTasks", ["smrttasks.admin"]) && (
                  <Link
                    to="/manage-smrt-tasks"
                    onClick={() => setAdminOpen(false)}
                  >
                    Manage SMRT Tasks
                  </Link>
                )}
                
                {can('sMRTTimeTrack', ['sMRTTimeTrack.admin']) && (
                  <Link
                    to="/smrt-timetrack"
                    onClick={() => setAdminOpen(false)}
                  >
                    SMRT TimeTrack
                  </Link>
                )}

                {can("smrtAccess", ["smrtAccess"]) && (
                  <Link to="/smrt-access" onClick={() => setAdminOpen(false)}>
                    SMRT Access
                  </Link>
                )}

                {scaffoldedActive
                  .filter(m => m.adminRoute && m.adminPermission)
                  .map(m => {
                    // Mirror the deriveModulePermissionRows naming rule: the
                    // "Manage" prefix only distinguishes the admin entry from
                    // a user-side entry. Admin-only modules don't need it.
                    const isAdminOnly = !m.route && !m.viewPermission;
                    const label = isAdminOnly ? m.name : `Manage ${m.name}`;
                    return can(m.entitlementKey, [m.adminPermission!]) && (
                      <Link key={m.id} to={m.adminRoute!} onClick={() => setAdminOpen(false)}>
                        {label}
                      </Link>
                    );
                  })}

              </div>
            )}
          </div>
        )}

        {isDev && !isOnProtectedRoute && isAuthenticated === false && (
          <div className="auth-buttons">
            <button className="signup-button" onClick={handleSignup}>
              Sign Up
            </button>
            <button className="login-button" onClick={handleLogin}>
              Log In
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
